import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Toast, Translations } from '@core/services';
import { ApplicationsService } from '@core/services/applications.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	of,
	switchMap,
	tap
} from 'rxjs';
import { App } from '../../../definitions/app-structure';
import {
	loadDatabases,
	loadDatabasesFailure,
	loadDatabasesSuccess,
	refreshDatabases
} from '@store/actions';
import {
	appLoaded,
	loadAppFailure,
	loadAppSuccess
} from '@store/actions/app.actions';
import { currentApp } from '@store/reducers';

@Injectable()
export class AppEffects {
	#store = inject(Store);
	#router = inject(Router);
	#applicationsSvc = inject(ApplicationsService);
	#actions$ = inject(Actions);

	loadAppFailure$ = createEffect(
		() =>
			this.#actions$.pipe(
				ofType(loadAppFailure),
				tap(({ error }) => {
					Toast.error(
						Translations.instant('GENERIC.MESSAGES.ERROR.LOADING_ITEMS')
					);
					this.#router.navigate(['./apps']);
				})
			),
		{ dispatch: false }
	);

	loadAppSucces$ = createEffect(
		() =>
			this.#actions$.pipe(
				ofType(appLoaded),
				tap(({ app }) => {
					this.#store.dispatch(loadAppSuccess({ app }));
				}),
				catchError((error) => {
					this.#store.dispatch(loadAppFailure({ error }));

					return of(null);
				})
			),
		{ dispatch: false }
	);

	// DATABASES
	loadDatabases$ = createEffect(() =>
		this.#actions$.pipe(
			ofType(loadDatabases),
			debounceTime(64),
			switchMap(() => this.#store.select(currentApp)),
			distinctUntilChanged((prevApp: App, currentApp: App) => {
				if (prevApp?.id !== currentApp?.id) {
					return false;
				}

				const prevDatabaseIds = prevApp?.databaseIds ?? [];
				const currentDatabaseIds = currentApp?.databaseIds ?? [];

				if (prevDatabaseIds.length !== currentDatabaseIds.length) {
					return false;
				}

				return (
					prevDatabaseIds.every((id) => currentDatabaseIds.includes(id)) &&
					currentDatabaseIds.every((id) => prevDatabaseIds.includes(id))
				);
			}),
			filter((app) => !!app),
			switchMap(({ id: appId }) => {
				return this.#appDatabases(appId);
			})
		)
	);

	refreshDatabases$ = createEffect(() =>
		this.#actions$.pipe(
			ofType(refreshDatabases),
			debounceTime(64),
			switchMap(() => this.#store.select(currentApp)),
			switchMap(({ id: appId }) => {
				return appId ? this.#appDatabases(appId) : [];
			})
		)
	);

	#appDatabases(appId: number) {
		return this.#applicationsSvc.databasesOfAppId(appId).pipe(
			map((databases) => loadDatabasesSuccess({ databases })),
			catchError((error) => {
				console.error('Error loading databases:', error);

				// Determine error type and show appropriate message
				let errorMessage: string;
				if (error.status === 404) {
					errorMessage = Translations.instant(
						'DATABASES_RESOLVER.ERRORS.APP_NOT_FOUND',
						{ appId }
					);
				} else if (error.status === 403) {
					errorMessage = Translations.instant(
						'DATABASES_RESOLVER.ERRORS.ACCESS_DENIED'
					);
				} else if (error.status === 0) {
					errorMessage = Translations.instant(
						'DATABASES_RESOLVER.ERRORS.NETWORK_ERROR'
					);
				} else {
					errorMessage = Translations.instant(
						'DATABASES_RESOLVER.ERRORS.GENERIC',
						{
							errorCode: error.status || 'UNKNOWN'
						}
					);
				}

				// For critical errors, redirect to error page
				if (error.status === 404 || error.status === 403) {
					this.#router.navigate(['/error']);
				}

				Toast.error(errorMessage);
				return of(loadDatabasesFailure({ error }));
			})
		);
	}
}
